import {
  Box, Checkbox, checkboxClasses, IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow, Tooltip, Typography, useTheme
} from '@mui/material';
import { IPagination, ITableColumn, ITableProps } from '../../interfaces/tableProps';
import React, { useCallback, useEffect, useMemo } from 'react';
import { ContentCopy, FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from '@mui/icons-material';
import { FlexBox, FlexBoxFullWidth } from './boxes';
import { useNotification } from '../../+state/useNotification';
import { TEXTS } from '../../core/texts';
import { getTruncatedName } from '../../utils/getTruncatedName';

const AppTable = ({
                    data: { pagination, list, uniqueKey = 'id' },
                    columns,
                    actions,
                    handlers = {},
                    labelRowsPerPage,
                    checkbox,
                    pageNamespace = 'page',
                    sizeNamespace = 'size',
                  }: ITableProps) => {
  const { page, size, sizeOptions, totalCount, setState } = pagination || {} as IPagination;
  const { openInfoToast } = useNotification();
  const theme = useTheme();

  const availableActions = useMemo(() => actions?.filter(action => !action.condition || list.find(item => action.condition?.(item))), [list, actions]);

  const columnPropsWithActions: ITableColumn[] = useMemo(() =>
      columns.concat(availableActions?.length ? {
        name: '__actions',
        label: 'Actions',
        align: 'center'
      } : []).concat(checkbox ? {
        name: '__checkbox',
        label: 'Select',
        align: 'center'
      } : [])
    , [columns, availableActions, checkbox]);

  const handlePageChange = useCallback((_: any, newPage: number) => {
    setState((prev: any) => ({ ...prev, [pageNamespace]: newPage >= 0 ? newPage : page - 1 }));
  }, [setState, page, pageNamespace]);
  const handleChangeRowsPerPage = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setState((prev: any) => ({ ...prev, [sizeNamespace]: parseInt(event.target.value), [pageNamespace]: 0 }));
  }, [setState, pageNamespace, sizeNamespace]);

  useEffect(() => {
    if (!list.length && !!page) {
      handlePageChange(null, 0);
    }
  }, [list, page, handlePageChange]);

  const getColValue = useCallback((col: ITableColumn, val: string | number): string => col.format
    ? col.format(val)
    : val?.toString() || '', []);

  const copyToClipboardHandler = useCallback((val: string) => {
    navigator.clipboard.writeText(val);
    openInfoToast(TEXTS.COPIED_TO_CLIPBOARD, 1000);
  }, [openInfoToast]);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {
        list.length >= 10 && pagination &&
        <TablePagination
          component={Box}
          rowsPerPageOptions={sizeOptions}
          count={totalCount}
          page={(page <= 0 || totalCount < 0) ? 0 : (page > Math.ceil(totalCount / size)) ? Math.ceil(totalCount / size) : page}
          rowsPerPage={size}
          labelRowsPerPage={labelRowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      }
      <TableContainer>
        <Table stickyHeader={true}>
          <TableHead>
            <TableRow>
              {
                columnPropsWithActions.map((col) => (
                  <TableCell key={col.name} align={col.align}
                             style={{ minWidth: col.minWidth, maxWidth: col.maxWidth }}>
                    {col.label}
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              !!list.length && list.map((item, idx) => (
                <TableRow
                  key={item[uniqueKey] + idx.toString()}
                  hover={checkbox ? !checkbox.checked(item) : true}
                  sx={{ background: checkbox && checkbox.checked(item) ? theme.palette.primary.main : 'initial' }}
                >
                  {
                    columns.map(col => {
                      const val = getColValue(col, item[col.name] as string | number);
                      const handler = handlers[col.name];
                      return (
                        <TableCell key={col.name} align={col.align}>
                          <FlexBox alignItems={'center'}>
                            <Tooltip title={col.truncate && val.length > col.truncate ? val : ''}>
                              <Box onClick={handler ? () => handler(item) : undefined}
                                   sx={{ color: checkbox && checkbox.checked(item) ? theme.palette.background.default : 'initial', ...col.sx }}
                              >
                                {col.truncate ? getTruncatedName(val, col.truncate) : val}
                              </Box>
                            </Tooltip>
                            {
                              col.withCopyIcon &&
                              <Tooltip title={'Copy'}>
                                <IconButton onClick={() => copyToClipboardHandler(val)} sx={{ ml: .5, px: 1, py: .5 }}>
                                  <ContentCopy sx={{
                                    width: 16,
                                    color: checkbox && checkbox.checked(item) ? theme.palette.background.default : 'initial'
                                  }}/>
                                </IconButton>
                              </Tooltip>
                            }
                          </FlexBox>
                        </TableCell>
                      );
                    })
                  }
                  {
                    !!availableActions?.length &&
                    <TableCell key={'__actions' + item[uniqueKey] + idx.toString()} align={'center'}>
                      <FlexBoxFullWidth alignItems={'center'} justifyContent={'flex-end'} flexWrap={'nowrap'}>
                        {
                          availableActions
                            .filter(action => !action.condition || action.condition(item))
                            .map((action, index) => (
                              <Tooltip title={action.tooltip || ''} key={'__actions' + index + action.tooltip || ''}>
                                <IconButton onClick={() => action.onClick(item)}>
                                  {action.icon}
                                </IconButton>
                              </Tooltip>
                            ))
                        }
                      </FlexBoxFullWidth>
                    </TableCell>
                  }
                  {
                    !!checkbox &&
                    <TableCell key={'__checkbox' + item[uniqueKey] + idx.toString()} align={'center'}>
                      <Checkbox checked={checkbox.checked(item)} onChange={() => checkbox.onChange(item)}
                                sx={{ [`&.${checkboxClasses.checked}`]: { color: 'white' } }}

                      />
                    </TableCell>
                  }
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
      {
        !list.length
          ? <FlexBoxFullWidth justifyContent={'center'} mt={10}>
            <Typography>{'No records found'}</Typography>
          </FlexBoxFullWidth>
          : pagination
            ? <TablePagination
              component={Box}
              rowsPerPageOptions={sizeOptions}
              count={totalCount}
              page={(page <= 0 || totalCount < 0) ? 0 : (page > Math.ceil(totalCount / size)) ? Math.ceil(totalCount / size) : page}
              rowsPerPage={size}
              labelRowsPerPage={labelRowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
            : null
      }
    </Paper>
  );
};

export default AppTable;

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

function TablePaginationActions({ count, page, rowsPerPage, onPageChange }: TablePaginationActionsProps) {
  const theme = useTheme();
  const handleFirstPageButtonClick = useCallback((
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  }, [onPageChange]);

  const handleBackButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  }, [onPageChange, page]);

  const handleNextButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  }, [onPageChange, page]);

  const handleLastPageButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  }, [onPageChange, count, rowsPerPage]);
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPage/> : <FirstPage/>}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight/> : <KeyboardArrowLeft/>}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft/> : <KeyboardArrowRight/>}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPage/> : <LastPage/>}
      </IconButton>
    </Box>
  );
}